// Version history for the file open in the editor: a right-side panel
// listing its Drive versions (newest first) with Download and Restore.
// Restoring asks Drive for a new version holding the old bytes; the page
// then reloads the document. Identical file in Docs, Sheets and Slides.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Download, History, Loader2, RotateCcw, X } from "@fg/icons";
import { BTN_DANGER, BTN_GHOST, BTN_PRIMARY, Modal, cx } from "@/features/home/HomeUi";
import { driveFileApi, errorText, formatBytes, formatWhen, type FileVersion } from "./driveFileApi";
import type { HeaderTone } from "./ShareDialog";

export const HISTORY_SHORTCUT = "Ctrl+Alt+Shift+H";
export const VIEWER_HISTORY_MESSAGE = "Version history is available to the file's owner and editors.";

/** Ctrl+Alt+Shift+H (⌘ on a Mac) — opens the panel. */
export function isHistoryShortcut(e: KeyboardEvent): boolean {
    return (e.ctrlKey || e.metaKey) && e.altKey && e.shiftKey && (e.code === "KeyH" || e.key.toLowerCase() === "h");
}

const SMALL_BTN =
    "inline-flex h-7 items-center gap-1 rounded-md border border-input bg-background px-2 text-xs font-medium hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

/** Clock button next to the save status. */
export function HistoryButton({
    onClick, disabledReason, tone
}: {
    onClick: () => void;
    /** Set → the button is inert and this is its tooltip. */
    disabledReason?: string | null;
    tone: HeaderTone;
}) {
    const disabled = Boolean(disabledReason);
    return (
        <button
            type="button"
            onClick={() => { if (!disabled) onClick(); }}
            aria-label="Version history"
            aria-haspopup="dialog"
            aria-disabled={disabled || undefined}
            aria-keyshortcuts="Control+Alt+Shift+H"
            title={disabledReason ?? `Version history (${HISTORY_SHORTCUT})`}
            className={tone === "onPrimary"
                // Suite top bar: `.suite-appbar-btn` follows its Accent / Light / Dark style.
                ? cx(
                    "suite-appbar-btn inline-flex h-8 w-8 shrink-0 items-center justify-center rounded",
                    disabled && "cursor-not-allowed opacity-50 hover:bg-transparent"
                )
                : cx(
                    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    disabled ? "cursor-not-allowed opacity-50" : "hover:bg-accent hover:text-foreground"
                )}
        >
            <History className="h-4 w-4" aria-hidden />
        </button>
    );
}

export interface VersionHistoryPanelProps {
    file: { id: string; name: string };
    onClose: () => void;
    /** Re-fetches whenever this changes — pass the save status so new saves show up. */
    refreshKey?: unknown;
    /** A save is in flight — restoring now would race it. */
    saving: boolean;
    hasUnsavedChanges: () => boolean;
    /**
     * Restores the version in Drive and reloads it into the editor.
     * `discardChanges` = the user chose to drop unsaved edits. Rejects with
     * a user-facing message.
     */
    onRestore: (versionId: string, discardChanges: boolean) => Promise<void>;
}

interface PendingRestore {
    version: FileVersion;
    /** Captured when Restore was clicked. */
    unsaved: boolean;
}

export function VersionHistoryPanel({
    file, onClose, refreshKey, saving, hasUnsavedChanges, onRestore
}: VersionHistoryPanelProps) {
    const auth = useAuth();
    const tokenRef = useRef(auth.user?.access_token);
    tokenRef.current = auth.user?.access_token;
    const titleId = useId();
    const closeRef = useRef<HTMLButtonElement>(null);

    const [versions, setVersions] = useState<FileVersion[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [pending, setPending] = useState<PendingRestore | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [message, setMessage] = useState<{ tone: "info" | "error"; text: string } | null>(null);

    const load = useCallback(async () => {
        try {
            setVersions(await driveFileApi.versions(file.id, tokenRef.current));
            setLoadError(null);
        } catch (err) {
            setLoadError(errorText(err));
        }
    }, [file.id]);

    // Another file opened while the panel is up — don't show the old list.
    useEffect(() => {
        setVersions(null);
        setMessage(null);
    }, [file.id]);
    useEffect(() => { void load(); }, [load, refreshKey]);

    // Focus the panel on open; hand focus back to whatever opened it.
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        closeRef.current?.focus();
        return () => {
            if (previous && document.contains(previous)) previous.focus();
        };
    }, []);

    const download = async (v: FileVersion) => {
        setDownloadingId(v.id);
        setMessage(null);
        try {
            startNativeDownload(await driveFileApi.versionDownloadUrl(file.id, v.id, tokenRef.current));
        } catch (err) {
            setMessage({ tone: "error", text: `Couldn't download version ${v.versionNumber}: ${errorText(err)}` });
        } finally {
            setDownloadingId(null);
        }
    };

    const confirmRestore = async (discard: boolean) => {
        if (!pending) return;
        const n = pending.version.versionNumber;
        await onRestore(pending.version.id, discard);
        setPending(null);
        setMessage({ tone: "info", text: `Restored version ${n} — it's now the current version.` });
        await load();
    };

    return (
        <aside
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            onKeyDown={(e) => {
                if (e.key === "Escape") {
                    e.stopPropagation();
                    onClose();
                }
            }}
            className="fixed inset-y-0 right-0 z-[65] flex w-full max-w-sm flex-col border-l bg-background text-foreground shadow-2xl"
        >
            <header className="flex h-14 shrink-0 items-center gap-2 border-b px-3">
                <History className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <div className="min-w-0 flex-1">
                    <h2 id={titleId} className="truncate text-sm font-semibold">Version history</h2>
                    <p className="truncate text-xs text-muted-foreground" title={file.name}>{file.name}</p>
                </div>
                <button
                    ref={closeRef}
                    type="button"
                    onClick={onClose}
                    aria-label="Close version history"
                    title="Close (Esc)"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <X className="h-4 w-4" aria-hidden />
                </button>
            </header>
            <p className="shrink-0 border-b px-3 py-2 text-xs text-muted-foreground">
                Every save to Drive keeps a version. Restoring one adds it as a new version — nothing is overwritten.
            </p>
            {message ? (
                <p
                    role={message.tone === "error" ? "alert" : "status"}
                    className={cx(
                        "shrink-0 border-b px-3 py-2 text-xs",
                        message.tone === "error" ? "text-destructive" : "text-emerald-700 dark:text-emerald-400"
                    )}
                >
                    {message.text}
                </p>
            ) : null}

            <div className="min-h-0 flex-1 overflow-y-auto">
                {versions === null ? (
                    loadError ? (
                        <div className="flex flex-col items-start gap-2 p-4 text-sm">
                            <p role="alert" className="text-destructive">{loadError}</p>
                            <button type="button" onClick={() => { setLoadError(null); void load(); }} className={SMALL_BTN}>
                                Retry
                            </button>
                        </div>
                    ) : (
                        <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
                        </p>
                    )
                ) : versions.length === 0 ? (
                    <p className="p-4 text-sm text-muted-foreground">No versions yet — save to Drive to create one.</p>
                ) : (
                    <ol aria-label="Versions, newest first" className="divide-y">
                        {versions.map((v, i) => {
                            const current = i === 0;
                            return (
                                <li
                                    key={v.id}
                                    aria-current={current ? "true" : undefined}
                                    className={cx("flex flex-col gap-1 px-3 py-2.5 text-sm", current && "bg-primary/5")}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">Version {v.versionNumber}</span>
                                        {current ? (
                                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                                                Current
                                            </span>
                                        ) : null}
                                        <span className="ml-auto text-xs text-muted-foreground">{formatBytes(v.sizeBytes)}</span>
                                    </div>
                                    <div
                                        className="truncate text-xs text-muted-foreground"
                                        title={`${new Date(v.createdAt).toLocaleString()} · ${v.createdBy}`}
                                    >
                                        <time dateTime={v.createdAt}>{formatWhen(v.createdAt)}</time> · {v.createdBy}
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1.5">
                                        <button
                                            type="button"
                                            onClick={() => void download(v)}
                                            disabled={downloadingId === v.id}
                                            aria-label={`Download version ${v.versionNumber}`}
                                            className={SMALL_BTN}
                                        >
                                            {downloadingId === v.id
                                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                                : <Download className="h-3.5 w-3.5" aria-hidden />}
                                            Download
                                        </button>
                                        {!current ? (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMessage(null);
                                                    setPending({ version: v, unsaved: hasUnsavedChanges() });
                                                }}
                                                disabled={saving}
                                                title={saving ? "Wait for the save to finish" : undefined}
                                                aria-label={`Restore version ${v.versionNumber}`}
                                                className={SMALL_BTN}
                                            >
                                                <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                                                Restore
                                            </button>
                                        ) : null}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>

            {pending ? (
                <RestoreDialog pending={pending} onCancel={() => setPending(null)} onConfirm={confirmRestore} />
            ) : null}
        </aside>
    );
}

function RestoreDialog({
    pending, onCancel, onConfirm
}: {
    pending: PendingRestore;
    onCancel: () => void;
    onConfirm: (discard: boolean) => Promise<void>;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const v = pending.version;

    const run = async (discard: boolean) => {
        setBusy(true);
        setError(null);
        try {
            await onConfirm(discard);
        } catch (err) {
            setError(errorText(err));
            setBusy(false);
        }
    };

    return (
        <Modal
            title={`Restore version ${v.versionNumber}?`}
            onClose={() => { if (!busy) onCancel(); }}
            footer={
                <>
                    <button type="button" onClick={onCancel} disabled={busy} className={BTN_GHOST} data-autofocus>
                        Cancel
                    </button>
                    {pending.unsaved ? (
                        <button type="button" onClick={() => void run(true)} disabled={busy} className={BTN_DANGER}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                            Discard my changes and restore
                        </button>
                    ) : (
                        <button type="button" onClick={() => void run(false)} disabled={busy} className={BTN_PRIMARY}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <RotateCcw className="h-4 w-4" aria-hidden />}
                            Restore
                        </button>
                    )}
                </>
            }
        >
            <div className="flex flex-col gap-2 p-4 text-sm">
                {pending.unsaved ? (
                    <>
                        <p className="font-medium">You have unsaved changes.</p>
                        <p className="text-muted-foreground">
                            Restoring replaces what's in the editor with version {v.versionNumber}. To keep your
                            changes, cancel and save first (Ctrl+S) — that save becomes a version you can come back to.
                        </p>
                    </>
                ) : (
                    <p className="text-muted-foreground">
                        Version {v.versionNumber} ({formatWhen(v.createdAt)}, {v.createdBy}) becomes the current version
                        and opens in the editor. Nothing is overwritten — every version stays in the history.
                    </p>
                )}
                {error ? <p role="alert" className="text-destructive">{error}</p> : null}
            </div>
        </Modal>
    );
}

/** Hands a short-lived anonymous URL to the browser's own downloader. */
function startNativeDownload(url: string): void {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
}
